/**
 * Site URL Utilities (VUL-003 Security Fix)
 *
 * Generates preview URLs using subdomain isolation.
 * This prevents XSS attacks from accessing other users' data by ensuring
 * each published site has its own origin.
 *
 * Example:
 *   getPreviewUrl('my-business-123') -> 'https://my-business-123.preview.sayfai.com'
 */

/**
 * Base domain for preview subdomains
 * Configurable via REACT_APP_PREVIEW_SUBDOMAIN_BASE environment variable
 */
const PREVIEW_SUBDOMAIN_BASE = process.env.REACT_APP_PREVIEW_SUBDOMAIN_BASE || 'preview.localhost';

/**
 * Generate the preview URL for a site
 * @param {string} siteDomain - The site's domain/slug (e.g., 'my-business-123')
 * @returns {string} Full preview URL
 */
export function getPreviewUrl(siteDomain) {
  if (!siteDomain) return '#';
  return `https://${siteDomain}.${PREVIEW_SUBDOMAIN_BASE}`;
}

/**
 * Generate preview URL with a specific page
 * @param {string} siteDomain - The site's domain/slug
 * @param {string} page - Page slug (e.g., 'about', 'contact')
 * @param {string|null} language - Language code (e.g., 'tr', 'es') or null for default
 * @returns {string} Full preview URL with page path
 */
export function getPreviewPageUrl(siteDomain, page, language = null) {
  let url = getPreviewUrl(siteDomain);

  if (language) {
    url += `/${language}`;
  }

  if (page && page !== 'index') {
    url += `/${page}`;
  }

  return url;
}

/**
 * Check if subdomain isolation is enabled
 * @returns {boolean} True if subdomain isolation is enabled
 */
export function isSubdomainIsolationEnabled() {
  // In development, check for localhost or preview.localhost
  return PREVIEW_SUBDOMAIN_BASE && PREVIEW_SUBDOMAIN_BASE !== '';
}

/**
 * Get the preview subdomain base
 * @returns {string} The preview subdomain base (e.g., 'preview.sayfai.com')
 */
export function getPreviewSubdomainBase() {
  return PREVIEW_SUBDOMAIN_BASE;
}

export default {
  getPreviewUrl,
  getPreviewPageUrl,
  isSubdomainIsolationEnabled,
  getPreviewSubdomainBase,
};
